import React, { useEffect } from 'react';
import styled from 'astroturf/react';

import { trackClick } from 'js/utils/trackClick';
import {
  PackPreviewRows,
  PackPreviewFade,
} from 'js/liswp_blocks/shared/PackPreviewRows';
import { Work as SidebarWork } from 'js/liswp/Sidebar/hooks/types';
import { formatStat } from 'js/liswp_blocks/shared/formatStat';
import {
  PACK_CLICK_SOURCE,
  PDF_CLICK_SOURCE,
  IMPRESSION_SOURCE,
  THUMBNAIL_CLICK_SOURCE,
} from 'js/single_work/loswp/shared/clickSources';
import { HeroWorkCardHeader } from 'js/single_work/loswp/shared/HeroWorkCardHeader';
import { useWorkViews } from 'js/single_work/loswp/shared/useWorkViews';
import { openDownloadModal } from 'js/single_work/loswp/download_modal/openDownloadModal';

import { CurrentPaperRow } from './CurrentPaperRow';
import { DownloadOptionCard } from './DownloadOptionCard';

type PackRow = {
  id: number;
  title: string;
  image?: string;
  authors: Array<{ displayName: string }>;
  publicationYear?: number;
  viewCount?: number;
};

export type LoswpPickDownloadHeroProps = {
  workId: number;
  paperTitle: string;
  authors: Array<{ displayName: string }>;
  publicationName?: string;
  publicationYear?: number;
  thumbnailUrl?: string;
  attachmentId?: number;
  pack: {
    priceCopy: string;
    totalCount: number;
    relatedWorks: PackRow[];
  };
};

const Section = styled.section`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: var(--spacing-md-40, 40px);
  color: var(--text-primary, #141413);

  @include breakpoint(small, medium) {
    padding: var(--spacing-md-40, 40px) var(--spacing-xs-16, 16px) 0;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1086px;
`;

const SectionHeading = styled.h2`
  @import 'stylesheets/design_system/breakpoint';

  margin: 0;
  padding-bottom: var(--spacing-sm-24, 24px);
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-sans-serif-md-size, 28px);
  line-height: var(--type-sans-serif-md-line-height, 34px);
  font-weight: var(--type-sans-serif-md-font-weight, 400);
  color: var(--text-primary, #141413);

  @include breakpoint(small, medium) {
    padding: var(--spacing-sm-24, 24px) 0;
    font-size: var(--type-body-md-bold-size, 16px);
    line-height: var(--type-body-md-bold-line-height, 20px);
    font-weight: var(--type-body-md-bold-font-weight, 600);
  }
`;

const CardsRow = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md-48, 48px);

  @include breakpoint(small, medium) {
    flex-direction: column;
    gap: var(--spacing-sm-24, 24px);
  }
`;

const LoswpPickDownloadHero: React.FC<LoswpPickDownloadHeroProps> = ({
  workId,
  paperTitle,
  authors,
  publicationName,
  publicationYear,
  thumbnailUrl,
  attachmentId,
  pack,
}) => {
  const hasPack = pack.totalCount > 0;

  const rowViews = useWorkViews(
    [workId, ...pack.relatedWorks.map((row) => row.id)],
    true,
  );

  useEffect(() => {
    trackClick({
      source: IMPRESSION_SOURCE,
      entityId: workId,
      extra: { source_work_id: workId },
    });
  }, [workId]);

  const openModal = (mode: 'pdf_only' | 'pdf_pack') => {
    openDownloadModal({
      mode,
      workId,
      workTitle: paperTitle,
      authors,
      publicationName,
      publicationYear,
      thumbnailUrl,
      attachmentId,
      pack: {
        priceCopy: pack.priceCopy,
        totalCount: pack.totalCount,
        relatedWorks: pack.relatedWorks,
        rowViews,
      },
    });
  };

  // Free single-PDF download entry point. Opens the download modal. The paid pack
  // CTA (onPackCta) is unaffected.
  const startPdfDownload = () => {
    openModal('pdf_only');
  };

  const onPdfCta = () => {
    trackClick({
      source: PDF_CLICK_SOURCE,
      entityId: workId,
      extra: { source_work_id: workId },
    });
    startPdfDownload();
  };

  const onPackCta = () => {
    trackClick({
      source: PACK_CLICK_SOURCE,
      entityId: workId,
      extra: { source_work_id: workId },
    });
    openModal('pdf_pack');
  };

  const onThumbnailCta = () => {
    trackClick({
      source: THUMBNAIL_CLICK_SOURCE,
      entityId: workId,
      extra: { source_work_id: workId },
    });
    startPdfDownload();
  };

  const onSurfaceClick =
    (handler: () => void) => (event: React.MouseEvent<HTMLDivElement>) => {
      if ((event.target as HTMLElement).closest('button, a')) return;
      handler();
    };

  const currentPaper = (
    <CurrentPaperRow
      title={paperTitle}
      thumbnailUrl={thumbnailUrl}
      publicationYear={publicationYear}
      viewCount={rowViews[String(workId)]}
    />
  );

  const rowYears = Object.fromEntries(
    pack.relatedWorks
      .filter((row) => row.publicationYear != null)
      .map((row) => [String(row.id), row.publicationYear as number]),
  );

  return (
    <Section>
      <Column>
        <HeroWorkCardHeader
          workId={workId}
          paperTitle={paperTitle}
          authors={authors}
          publicationName={publicationName}
          publicationYear={publicationYear}
          thumbnailUrl={thumbnailUrl}
          showBookmark={false}
          onThumbnailClick={onThumbnailCta}
          thumbnailClickLabel="Download PDF"
          bottomPadding={32}
        />
        <SectionHeading>Download options</SectionHeading>
        <CardsRow>
          <DownloadOptionCard
            title="PDF only"
            sublabel="1 PDF • Free"
            ctaLabel="Download PDF"
            onCta={onPdfCta}
            onSurfaceClick={onSurfaceClick(onPdfCta)}
            layout={hasPack ? undefined : 'half'}
            currentPaper={currentPaper}
          />
          {hasPack && (
            <DownloadOptionCard
              title="Full PDF Pack"
              sublabel={`${formatStat(pack.totalCount + 1)} PDFs • ${pack.priceCopy}`}
              ctaLabel={`Download PDF Pack for ${pack.priceCopy}`}
              ctaLabelMobile="Download PDF Pack"
              onCta={onPackCta}
              onSurfaceClick={onSurfaceClick(onPackCta)}
              currentPaper={currentPaper}
              rows={
                <>
                  <PackPreviewRows
                    variant="pickDownload"
                    relatedWorks={pack.relatedWorks as unknown as SidebarWork[]}
                    rowViews={rowViews}
                    rowYears={rowYears}
                    boldTitles
                    thumbChips
                    meta="yearviews"
                  />
                  <PackPreviewFade relatedCount={pack.totalCount} />
                </>
              }
            />
          )}
        </CardsRow>
      </Column>
    </Section>
  );
};

export default LoswpPickDownloadHero;
